import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { MARKET_PAGE_SIZE, MARKET_SORTS, marketFiltersToQuery, parseMarketFilters, type MarketFilters } from "../../shared/market";
import { Pagination } from "../components/Pagination";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { rarityLabel } from "../../shared/rarity";
import { useI18n } from "../i18n/I18nProvider";
import { useCatalog } from "../lib/CatalogProvider";
import { useAsync } from "../lib/useAsync";
import { marketApi } from "./api";
import { ListingGrid, ListingRows, offersLink, useListingView, ViewToggle } from "./components";
import { MarketFiltersForm } from "./MarketFiltersForm";
import { SellerMatches } from "./SellerMatches";
import { useGame } from "../lib/GameProvider";
import { gameFinishes } from "../../shared/games";

/** /mercado — community listings with seller, reputation, card and delivery filters. */
export function MarketPage() {
  const { t, locale, pick } = useI18n();
  const { categories } = useCatalog().catalog;
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [view, setView] = useListingView();
  const filters = useMemo(() => parseMarketFilters(params), [params]);
  const { game } = useGame();
  // A finish the game doesn't have (a shared link) would empty the list behind a filter that isn't shown.
  const asked = { ...filters, game, finish: filters.finish && gameFinishes(game).includes(filters.finish) ? filters.finish : null };
  const key = marketFiltersToQuery(asked);
  const result = useAsync((signal) => marketApi.listings(asked, signal), [key]);
  const apply = (patch: Partial<MarketFilters>) => {
    setParams(new URLSearchParams(marketFiltersToQuery({ ...filters, page: 1, ...patch })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const totalPages = result.data ? Math.max(1, Math.ceil(result.data.total / MARKET_PAGE_SIZE)) : 1;
  // A card offered more than once opens its page: the card once, then every seller's offer (price, condition, language, finish).
  const groupLink = offersLink;
  const productName = filters.product ? result.data?.items[0]?.name : undefined;
  // Filters that arrive in links (the header's categories, a card's set and rarity): seen and undone here.
  const chips = [
    ...filters.categories.map((c) => ({ key: `c-${c}`, label: pick(categories.find((x) => x.slug === c)?.name ?? { es: c, en: c }), patch: { categories: filters.categories.filter((x) => x !== c) } })),
    ...filters.sets.map((v) => ({ key: `s-${v}`, label: v, patch: { sets: filters.sets.filter((x) => x !== v) } })),
    ...filters.rarities.map((r) => ({ key: `r-${r}`, label: rarityLabel(r, locale) ?? r, patch: { rarities: filters.rarities.filter((x) => x !== r) } })),
  ];

  return (
    <div className="container catalog market">
      {filters.product && (
        <button type="button" className="btn btn-link btn-sm" onClick={() => apply({ product: null, sort: "newest" })}>
          {t("mkt.allCards")}
        </button>
      )}
      <div className="catalog-head">
        <h1>{productName ?? (filters.seller ? t("mkt.titleSeller", { seller: filters.seller }) : t("mkt.title"))}</h1>
        <p className="muted">{t("mkt.intro")}</p>
        {result.data && <p className="muted" aria-live="polite">{t(result.data.grouped ? "mkt.resultsCards" : "mkt.results", { count: result.data.total })}</p>}
      </div>

      <div className="catalog-toolbar">
        <button type="button" className="btn btn-outline filters-toggle" aria-expanded={open} onClick={() => setOpen(true)}>
          {t("mkt.filters")}
        </button>
        <label className="sort-select">
          <span className="sr-only">{t("mkt.sort")}</span>
          <select value={filters.sort} onChange={(e) => apply({ sort: e.target.value as MarketFilters["sort"] })}>
            {MARKET_SORTS.map((s) => (
              <option key={s} value={s}>
                {t(`mkt.sort.${s}` as never)}
              </option>
            ))}
          </select>
        </label>
        <ViewToggle view={view} onChange={setView} />
        <Link to="/vender/solicitar" className="btn btn-link">
          {t("mkt.sellCta")}
        </Link>
      </div>

      <div className="catalog-layout">
        <aside className={open ? "catalog-sidebar is-open" : "catalog-sidebar"} aria-label={t("mkt.filters")}>
          <div className="sidebar-head">
            <h2>{t("mkt.filters")}</h2>
            <button type="button" className="icon-btn sidebar-close" aria-label={t("nav.close")} onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          {/* Re-mounted when the URL changes (or the categories arrive) so the inputs show the applied filters. */}
          <MarketFiltersForm
            key={`${key}|${categories.length}`}
            filters={filters}
            onApply={(patch) => (apply(patch), setOpen(false))}
            onClear={() => (setParams(new URLSearchParams()), setOpen(false))}
          />
        </aside>

        <section className="catalog-results">
          {filters.q && !filters.seller && <SellerMatches q={filters.q} />}
          {chips.length > 0 && (
            <ul className="active-filters" aria-label={t("filters.active")}>
              {chips.map((chip) => (
                <li key={chip.key}>
                  <button type="button" className="filter-chip" aria-label={t("filters.remove", { label: chip.label })} onClick={() => apply(chip.patch)}>
                    {chip.label} <span aria-hidden="true">✕</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {result.error ? (
            <ErrorMessage onRetry={result.reload} />
          ) : result.loading && !result.data ? (
            <Loading />
          ) : result.data && result.data.items.length === 0 ? (
            <div className="status">
              <p>{t("mkt.empty")}</p>
              <Link to="/cuenta/compras?tab=busco" className="btn btn-outline">
                {t("mkt.emptyWant")}
              </Link>
            </div>
          ) : result.data ? (
            <div className={result.loading ? "is-refreshing" : undefined}>
              {!!result.data.featured?.length && (
                <section className="featured-strip" aria-label={t("mkt.feature.stripTitle")}>
                  <h2 className="small-title">⭐ {t("mkt.feature.stripTitle")}</h2>
                  {view === "list" ? <ListingRows items={result.data.featured} featured /> : <ListingGrid items={result.data.featured} featured />}
                </section>
              )}
              {view === "list" ? <ListingRows items={result.data.items} groupLink={groupLink} /> : <ListingGrid items={result.data.items} groupLink={groupLink} />}
              <Pagination page={filters.page} totalPages={totalPages} onPage={(page) => apply({ page })} />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}
